import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import { useUser } from '../../contexts/UserContext';
import { StatCard } from '../../components/UI';
import ActiveFilters from '../users-page/components/ActiveFilters';
import PaymentsReceivableTable from './components/PaymentsReceivableTable';
import PaymentsPayableTable from './components/PaymentsPayableTable';
import PaymentEditModal from './components/PaymentEditModal';
import PaymentFilesModal from './components/PaymentFilesModal';
import { SearchAndFilterBar } from '../../components/search-and-filter-bar';
import ResponseModal from '../../components/modals/response-modal';
import EmptyState from '../../components/not-result';
import SEOHead from '../../components/SEO/SEOHead';
import paymentService from '../../services/paymentService';
import statsApi from '../../api/statsApi';
import { convertFormDateToUTC5 } from '../../utils/dateUtils';
import Loading from '../../components/loading-animation';
import './PaymentsPage.scss';
import PaymentWizard from './paymentwizard';
import {
  getUserRole,
  ACCOUNTING_ROLES,
  isAdminOrManager,
  canEditPayments,
  canViewPaymentsReceivable,
  canViewPaymentsPayable
} from '../../utils/roleAccess';

const ITEMS_PER_PAGE = 10;

const PAYMENT_TABS_ALL = [
  { value: 'receivable', label: 'Accounts Receivable' },
  { value: 'payable', label: 'Accounts Payable' }
];

// Transform stats to cards format
const transformStatsToCards = (stats, tabType) => {
  if (!stats) return [];
  
  if (stats.statCards) return stats.statCards;

  if (tabType === 'receivable') {
    return [
      {
        title: 'Total Outstanding',
        value: `$${(stats.totals?.outstanding || 0).toLocaleString()}`,
        trend: {
          type: stats.trend?.type || 'neutral',
          percentage: stats.trend?.percentage || 0,
          text: stats.trend?.text || 'vs last period'
        },
        variant: 'white',
        diagramType: 'progress',
        diagramData: {
          value: stats.collectionRate || 35,
          label: `${stats.collectionRate || 35}%`,
          size: 60
        }
      },
      {
        title: 'Total Collected',
        value: `$${(stats.totals?.totalPaid || 0).toLocaleString()}`,
        trend: {
          type: stats.collectedTrend?.type || 'positive',
          percentage: stats.collectedTrend?.percentage || 0,
          text: stats.collectedTrend?.text || 'vs last period'
        },
        variant: 'blue',
        diagramType: 'area',
        diagramData: {
          data: stats.collectedChart || [50, 90, 70, 70, 65, 70, 90],
          width: 125,
          height: 60
        }
      },
      {
        title: 'Overdue',
        value: `$${(stats.overdue?.totalAmount || 0).toLocaleString()}`,
        trend: {
          type: 'negative',
          percentage: stats.overdue?.count || 0,
          text: 'invoices overdue'
        },
        variant: 'white',
        diagramType: 'bar',
        diagramData: {
          data: stats.overdueChart || [100, 50, 40, 50, 90, 120],
          width: 125,
          height: 60
        }
      }
    ];
  } else {
    return [
      {
        title: 'Total Pending',
        value: `$${(stats.totals?.outstanding || 0).toLocaleString()}`,
        trend: {
          type: stats.trend?.type || 'neutral',
          percentage: stats.trend?.percentage || 0,
          text: stats.trend?.text || 'vs last period'
        },
        variant: 'white',
        diagramType: 'progress',
        diagramData: {
          value: stats.paymentRate || 35,
          label: `${stats.paymentRate || 35}%`,
          size: 60
        }
      },
      {
        title: 'Total Paid',
        value: `$${(stats.totals?.totalPaid || 0).toLocaleString()}`,
        trend: {
          type: stats.paidTrend?.type || 'positive',
          percentage: stats.paidTrend?.percentage || 0,
          text: stats.paidTrend?.text || 'vs last period'
        },
        variant: 'blue',
        diagramType: 'area',
        diagramData: {
          data: stats.paidChart || [50, 90, 70, 70, 65, 70, 90],
          width: 125,
          height: 60
        }
      },
      {
        title: 'Net Amount',
        value: `$${(stats.totals?.totalNetAmount || 0).toLocaleString()}`,
        trend: {
          type: stats.netTrend?.type || 'positive',
          percentage: stats.netTrend?.percentage || 0,
          text: stats.netTrend?.text || 'after deductions'
        },
        variant: 'white',
        diagramType: 'bar',
        diagramData: {
          data: stats.netChart || [100, 50, 40, 50, 90, 120],
          width: 125,
          height: 60
        }
      }
    ];
  }
};

const PaymentsPage = () => {
  const { getThemeColors } = useTheme();
  const { user } = useUser();
  const colors = getThemeColors();
  const role = getUserRole(user);
  const canEdit = canEditPayments(role);
  const canReceivable = canViewPaymentsReceivable(role);
  const canPayable = canViewPaymentsPayable(role);
  const canSeeTopStats = isAdminOrManager(role) || ACCOUNTING_ROLES.includes(role);
  const visiblePaymentTabs = PAYMENT_TABS_ALL.filter(
    (t) => (t.value === 'receivable' && canReceivable) || (t.value === 'payable' && canPayable)
  );
  const defaultTab =
    visiblePaymentTabs.length === 1
      ? visiblePaymentTabs[0].value
      : canReceivable
        ? 'receivable'
        : 'payable';

  const [selectedTab, setSelectedTab] = useState(defaultTab);

  useEffect(() => {
    const allowed = visiblePaymentTabs.map((t) => t.value);
    if (allowed.length && !allowed.includes(selectedTab)) {
      setSelectedTab(defaultTab);
    }
  }, [defaultTab, selectedTab, visiblePaymentTabs]);

  // Common state
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [payments, setPayments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statCards, setStatCards] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTopStats = async () => {
      if (!canSeeTopStats) {
        setStatCards([]);
        return;
      }

      try {
        const now = new Date();
        const year = now.getFullYear();
        const from = `${year}-01-01`;
        const to = `${year}-12-31`;

        const response = await statsApi.getStats({ period: 'range', from, to, scopeType: 'global', grain: 'month' });
        if (!isMounted) return;

        const apiResponse = response?.data || response;
        const stats = apiResponse?.data || apiResponse;
        const totals = stats?.totals || {};

        const totalReceivable = totals.totalReceivable || 0;
        const totalPayable = totals.totalPayable || 0;
        const totalProfit = totals.totalProfit || 0;

        const monthKey = `${year}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const normalizeKey = (key) => (typeof key === 'string' ? key.slice(0, 7) : '');
        const points = Array.isArray(stats?.points) ? stats.points : [];
        const pastAndCurrentPoints = points.filter((p) => normalizeKey(p?.periodKey) <= monthKey);
        const lastPoints = pastAndCurrentPoints.slice(-5);
        const receivableSeries = lastPoints.map((p) => Number(p?.totals?.totalReceivable || 0));
        const payableSeries = lastPoints.map((p) => Number(p?.totals?.totalPayable || 0));
        const profitSeries = lastPoints.map((p) => Number(p?.totals?.totalProfit || 0));

        setStatCards([
          {
            title: 'Total Receivable',
            value: `$${Number(totalReceivable).toLocaleString()}`,
            diagramType: 'area',
            diagramData: { data: receivableSeries, width: 125, height: 60 }
          },
          {
            title: 'Total Payable',
            value: `$${Number(totalPayable).toLocaleString()}`,
            variant: 'blue',
            diagramType: 'area',
            diagramData: { data: payableSeries, width: 125, height: 60 }
          },
          {
            title: 'Total Profit',
            value: `$${Number(totalProfit).toLocaleString()}`,
            diagramType: 'bar',
            diagramData: { data: profitSeries, width: 125, height: 60 }
          }
        ]);
      } catch (err) {
        if (!isMounted) return;
        console.error('Error fetching payments top stats:', err);
        setStatCards([]);
      }
    };

    fetchTopStats();

    return () => {
      isMounted = false;
    };
  }, [canSeeTopStats]);

  // Response modal state
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    message: '',
    status: 'success',
    title: ''
  });

  // Payment Wizard state (for adding new payments)
  const [wizardState, setWizardState] = useState({
    isOpen: false,
    mode: 'add',
    paymentData: null
  });

  // Payment Edit Modal state (for editing existing payments)
  const [editModalState, setEditModalState] = useState({
    isOpen: false,
    paymentData: null
  });

  // Payment Files Modal state (for viewing attached files)
  const [filesModalState, setFilesModalState] = useState({
    isOpen: false,
    paymentData: null
  });

  // Refs for preventing duplicate requests
  const isFetchingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const lastFetchParamsRef = useRef(null);

  const showResponseModal = (message, status = 'success', title = '') => {
    setResponseModal({
      isOpen: true,
      message,
      status,
      title
    });
  };

  const closeResponseModal = () => {
    setResponseModal(prev => ({ ...prev, isOpen: false }));
  };

  // Payment Wizard handlers
  const openAddWizard = () => {
    setWizardState({
      isOpen: true,
      mode: 'add',
      paymentData: null
    });
  };

  const openEditWizard = (payment) => {
    if (!canEdit) return;
    if (!payment || (!payment._id && !payment.id)) {
      showResponseModal('Invalid payment data', 'error', 'Error');
      return;
    }
    setEditModalState({
      isOpen: true,
      paymentData: payment
    });
  };

  const closeEditModal = () => {
    setEditModalState({
      isOpen: false,
      paymentData: null
    });
  };

  // Files Modal handlers
  const openFilesModal = (payment) => {
    setFilesModalState({
      isOpen: true,
      paymentData: payment
    });
  };

  const closeFilesModal = () => {
    setFilesModalState({
      isOpen: false,
      paymentData: null
    });
  };

  const handleEditModalSuccess = () => {
    showResponseModal('Payment updated successfully', 'success', 'Updated');
    fetchData(selectedTab, currentPage, searchTerm, filterValues);
  };

  const closeWizard = () => {
    setWizardState({
      isOpen: false,
      mode: 'add',
      paymentData: null
    });
  };

  const handleWizardSuccess = () => {
    showResponseModal('Payment created successfully', 'success', 'Created');
    fetchData(selectedTab, currentPage, searchTerm, filterValues);
  };

  const buildFilterParams = (values = {}, tab) => {
    const params = {};
    const setIf = (key, value) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'string' && value.trim() === '') return;
      params[key] = value;
    };

    setIf('status', values.status);
    setIf('paymentMethod', values.paymentMethod);

    if (values.invoiceDate) {
      params.invoiceAt = values.invoiceDate;
    }
    if (values.dueDate) {
      params.dueAt = values.dueDate;
    }
    if (values.paidDate) {
      params[tab === 'receivable' ? 'receivedAt' : 'paidAt'] = values.paidDate;
    }

    return params;
  };

  // Fetch data from API
  const fetchData = useCallback(async (tab, page, search, filters) => {
    // Create params signature to prevent duplicate requests
    const paramsSignature = JSON.stringify({ tab, page, search, filters });
    
    // Skip if same request is already in progress
    if (isFetchingRef.current && lastFetchParamsRef.current === paramsSignature) {
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    isFetchingRef.current = true;
    lastFetchParamsRef.current = paramsSignature;
    setLoading(true);
    setFetchError(null);

    try {
      // Build request parameters
      const params = {
        page,
        limit: ITEMS_PER_PAGE,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      const filterParams = buildFilterParams(filters, tab);
      const hasFilters = Object.keys(filterParams).length > 0;
      Object.assign(params, filterParams);

      const paymentsResponse = await (tab === 'receivable'
        ? (search.trim() || hasFilters
            ? paymentService.searchReceivables(search.trim(), params)
            : paymentService.getReceivables(params))
        : (search.trim() || hasFilters
            ? paymentService.searchPayables(search.trim(), params)
            : paymentService.getPayables(params)));
      
      // Top stat cards are fetched separately (global current year) and do not depend on filters/tabs.

      // Process payments - extract from axios response
      const paymentsApiResponse = paymentsResponse?.data || paymentsResponse;
      
      
      // Only treat as error if success is explicitly false or error field exists
      // If success is true or undefined, proceed with data extraction
      if (paymentsApiResponse?.success === false || paymentsApiResponse?.error) {
        const errorMsg = paymentsApiResponse?.error || 
                        paymentsApiResponse?.message || 
                        'Failed to fetch payments';
        console.error('API returned error:', errorMsg);
        setFetchError(errorMsg);
        setPayments([]);
        setTotalItems(0);
        return;
      }
      
      // Extract payments array from response
      // Server returns: { success: true, data: [...], pagination: {...} }
      const rawPayments = Array.isArray(paymentsApiResponse?.data) 
        ? paymentsApiResponse.data 
        : [];
      
      const pagination = paymentsApiResponse?.pagination || {};


      // Simply add UI state (expanded) to each payment - use data as-is from server
      // No filtering needed - just load what the API returns
      const processedPayments = rawPayments.map((payment, index) => {
        const processed = {
          ...payment,
          // Ensure both _id and id exist for compatibility (server returns 'id')
          _id: payment.id || payment._id || null,
          id: payment.id || payment._id || null,
          expanded: false // UI state for expand/collapse
        };
        
    
        return processed;
      });
      
      
      // Set payments and pagination - use data directly from server
      setPayments(processedPayments);
      setTotalItems(pagination.total ?? pagination.totalItems ?? processedPayments.length);
      setFetchError(null); // Always clear error on successful data load
      
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return;
      }
      
      console.error(`Error fetching ${tab} data:`, err);
      
      const errorMessage = err?.response?.data?.error || 
                          err?.response?.data?.message || 
                          err?.message || 
                          `Failed to fetch ${tab} data`;
      
      setFetchError(errorMessage);
      setPayments([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
      abortControllerRef.current = null;
    }
  }, []);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchData(selectedTab, currentPage, searchTerm, filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, currentPage, searchTerm, filterValues]);

  const handleTabChange = (newTab) => {
    if (newTab === selectedTab) return;
    if (!visiblePaymentTabs.some((t) => t.value === newTab)) return;
    setSelectedPayments([]);
    setCurrentPage(1);
    setSelectedTab(newTab);
  };

  const handleFilterApply = (filters) => {
    setFilterValues(filters);
    
    // Convert filter object to array of filter strings for ActiveFilters display
    const filterArray = [];
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== '' && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) {
          filterArray.push(`${key}: ${value.join(', ')}`);
        } else if (typeof value === 'boolean' && value) {
          filterArray.push(key);
        } else if (typeof value === 'string' && value.trim() !== '') {
          filterArray.push(`${key}: ${value}`);
        }
      }
    });
    setActiveFilters(filterArray);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterIndex) => {
    const filterToRemove = activeFilters[filterIndex];
    const newFilters = activeFilters.filter((_, index) => index !== filterIndex);
    setActiveFilters(newFilters);
    
    // Also update filterValues
    const updatedFilterValues = { ...filterValues };
    const key = filterToRemove.split(':')[0].trim();
    if (updatedFilterValues[key] !== undefined) {
      if (Array.isArray(updatedFilterValues[key])) {
        updatedFilterValues[key] = [];
      } else {
        updatedFilterValues[key] = '';
      }
      setFilterValues(updatedFilterValues);
    }
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
    setFilterValues({});
    setCurrentPage(1);
  };

  const handleSelectAll = (pageItemIds = []) => {
    if (pageItemIds.length === 0) {
      setSelectedPayments([]);
      return;
    }

    const hasSelectedAll = pageItemIds.every((id) =>
      selectedPayments.includes(id)
    );

    if (hasSelectedAll) {
      setSelectedPayments((prev) => prev.filter((id) => !pageItemIds.includes(id)));
    } else {
      setSelectedPayments((prev) => {
        const merged = new Set(prev);
        pageItemIds.forEach((id) => merged.add(id));
        return Array.from(merged);
      });
    }
  };

  const handleSelectPayment = (paymentId) => {
    setSelectedPayments(prev => 
      prev.includes(paymentId) 
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const handleDeleteSelected = async (idsToDelete) => {
    if (!idsToDelete || idsToDelete.length === 0) {
      if (selectedPayments.length === 0) return;
      idsToDelete = selectedPayments;
    }

    const validIds = idsToDelete.filter(id => id && (typeof id === 'string' || typeof id === 'object'));
    if (validIds.length === 0) {
      showResponseModal(
        'No valid payment IDs selected',
        'error',
        'Invalid Selection'
      );
      return;
    }

    try {
      setLoading(true);
      
      const deleteMethod = selectedTab === 'receivable'
        ? paymentService.deleteReceivable
        : paymentService.deletePayable;
      
      await Promise.all(
        validIds.map((id) => {
          const paymentId = typeof id === 'object' ? (id._id || id.id) : id;
          if (!paymentId) {
            return Promise.resolve();
          }
          return deleteMethod.call(paymentService, paymentId);
        })
      );
      
      showResponseModal(
        `Successfully deleted ${validIds.length} payment(s)`,
        'success',
        'Deleted'
      );
      fetchData(selectedTab, currentPage, searchTerm, filterValues);
      setSelectedPayments([]);
    } catch (err) {
      console.error("Error deleting payments:", err);
      const errorMessage = err?.response?.data?.error || 
                          err?.response?.data?.message || 
                          err?.message || 
                          "Failed to delete payments";
      showResponseModal(errorMessage, 'error', 'Delete Failed');
      setLoading(false);
    }
  };

  const handleToggleExpand = (paymentId) => {
    setPayments(prev => prev.map(payment => 
      (payment._id || payment.id) === paymentId 
        ? { ...payment, expanded: !payment.expanded }
        : payment
    ));
  };

  // Receivable-specific actions
  const handleCreateInvoice = async (paymentId, dueDate = null) => {
    if (!paymentId) {
      showResponseModal('Invalid payment ID', 'error', 'Error');
      return;
    }
    
    try {
      setLoading(true);
      const id = typeof paymentId === 'object' ? (paymentId._id || paymentId.id) : paymentId;
      // Convert dueDate to UTC-5 if provided
      const utc5DueDate = dueDate ? convertFormDateToUTC5(dueDate) : null;
      await paymentService.createInvoice(id, utc5DueDate);
      showResponseModal('Invoice created successfully', 'success', 'Invoice Created');
      fetchData(selectedTab, currentPage, searchTerm, filterValues);
    } catch (err) {
      const errorMessage = err?.response?.data?.error || 
                          err?.response?.data?.message || 
                          err?.message || 
                          'Failed to create invoice';
      showResponseModal(errorMessage, 'error', 'Error');
      setLoading(false);
    }
  };

  const handleRecordReceivablePayment = async (paymentId, paymentData) => {
    if (!paymentId) {
      showResponseModal('Invalid payment ID', 'error', 'Error');
      return;
    }
    
    try {
      setLoading(true);
      const id = typeof paymentId === 'object' ? (paymentId._id || paymentId.id) : paymentId;
      await paymentService.recordReceivablePayment(id, paymentData);
      showResponseModal('Payment recorded successfully', 'success', 'Payment Recorded');
      fetchData(selectedTab, currentPage, searchTerm, filterValues);
    } catch (err) {
      const errorMessage = err?.response?.data?.error || 
                          err?.response?.data?.message || 
                          err?.message || 
                          'Failed to record payment';
      showResponseModal(errorMessage, 'error', 'Error');
      setLoading(false);
    }
  };

  // Payable-specific actions
  const handleSchedulePayment = async (paymentId, scheduleData) => {
    if (!paymentId) {
      showResponseModal('Invalid payment ID', 'error', 'Error');
      return;
    }
    
    try {
      setLoading(true);
      const id = typeof paymentId === 'object' ? (paymentId._id || paymentId.id) : paymentId;
      await paymentService.schedulePayment(id, scheduleData);
      showResponseModal('Payment scheduled successfully', 'success', 'Payment Scheduled');
      fetchData(selectedTab, currentPage, searchTerm, filterValues);
    } catch (err) {
      const errorMessage = err?.response?.data?.error || 
                          err?.response?.data?.message || 
                          err?.message || 
                          'Failed to schedule payment';
      showResponseModal(errorMessage, 'error', 'Error');
      setLoading(false);
    }
  };

  const handleRecordPayablePayment = async (paymentId, paymentData) => {
    if (!paymentId) {
      showResponseModal('Invalid payment ID', 'error', 'Error');
      return;
    }
    
    try {
      setLoading(true);
      const id = typeof paymentId === 'object' ? (paymentId._id || paymentId.id) : paymentId;
      await paymentService.recordPayablePayment(id, paymentData);
      showResponseModal('Payment recorded successfully', 'success', 'Payment Recorded');
      fetchData(selectedTab, currentPage, searchTerm, filterValues);
    } catch (err) {
      const errorMessage = err?.response?.data?.error || 
                          err?.response?.data?.message || 
                          err?.message || 
                          'Failed to record payment';
      showResponseModal(errorMessage, 'error', 'Error');
      setLoading(false);
    }
  };

  const handleEditDeductions = async (paymentId, deductionsData) => {
    if (!paymentId) {
      showResponseModal('Invalid payment ID', 'error', 'Error');
      return;
    }
    
    try {
      setLoading(true);
      const id = typeof paymentId === 'object' ? (paymentId._id || paymentId.id) : paymentId;
      await paymentService.updateDeductions(id, deductionsData);
      showResponseModal('Deductions updated successfully', 'success', 'Deductions Updated');
      fetchData(selectedTab, currentPage, searchTerm, filterValues);
    } catch (err) {
      const errorMessage = err?.response?.data?.error || 
                          err?.response?.data?.message || 
                          err?.message || 
                          'Failed to update deductions';
      showResponseModal(errorMessage, 'error', 'Error');
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Payment Management"
        description="Manage receivables and payables efficiently. Track invoices, payments, financial transactions, and payment status for your logistics operations."
        keywords="payment management, invoice management, receivables, payables, logistics payments, payment tracking, financial management, invoice tracking"
        url="/payments"
      />
      <Container fluid style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
      <div className="payments-page">
        {/* Statistics Cards */}
        {statCards.length > 0 && (
          <div className="stat-cards">
            {statCards.map((card, index) => (
              <div className="stat-cards__wrapper" key={index}>
                <StatCard {...card} />
              </div>
            ))}
          </div>
        )}

        {visiblePaymentTabs.length > 1 && (
          <div className="payments-tabs">
            {visiblePaymentTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                className={`payments-tabs__item${
                  tab.value === selectedTab ? ' is-active' : ''
                }`}
                onClick={() => handleTabChange(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
        
        <SearchAndFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onFilterApply={handleFilterApply}
          pageType={selectedTab === 'receivable' ? 'paymentsReceivable' : 'paymentsPayable'}
          initialFilters={filterValues}
          searchPlaceholder={selectedTab === 'receivable' 
            ? "Search by invoice, customer..." 
            : "Search by load, carrier..."}
          disableAddModal
          showAddButton={canEdit}
          onAddClick={openAddWizard}
          addButtonText={`Add ${selectedTab === 'receivable' ? 'Receivable' : 'Payable'}`}
        />

        {activeFilters.length > 0 && (
          <ActiveFilters
            filters={activeFilters}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
          />
        )}

        {/* Content Area */}
        {loading && payments.length === 0 ? (
          <Loading size="large" />
        ) : fetchError ? (
          <EmptyState
            variant="error"
            title="Failed to load data"
            message={fetchError}
            onRetry={() => fetchData(selectedTab, currentPage, searchTerm, filterValues)}
            retryText="Retry"
          />
        ) : payments.length === 0 ? (
          <EmptyState
            variant={searchTerm ? "search" : "empty"}
            title={searchTerm ? "No results found" : `No ${selectedTab === 'receivable' ? 'receivables' : 'payables'} yet`}
            message={searchTerm 
              ? `No payments matching "${searchTerm}"` 
              : `No ${selectedTab === 'receivable' ? 'accounts receivable' : 'accounts payable'} records found`
            }
          />
        ) : selectedTab === 'receivable' ? (
          <PaymentsReceivableTable
            payments={payments}
            selectedPayments={selectedPayments}
            onSelectAll={handleSelectAll}
            onSelectPayment={handleSelectPayment}
            onToggleExpand={handleToggleExpand}
            onDeleteSelected={handleDeleteSelected}
            onEdit={openEditWizard}
            onViewFiles={openFilesModal}
            onCreateInvoice={handleCreateInvoice}
            onRecordPayment={handleRecordReceivablePayment}
            canSelect={canEdit}
            canDelete={canEdit}
            canEdit={canEdit}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        ) : (
          <PaymentsPayableTable
            payments={payments}
            selectedPayments={selectedPayments}
            onSelectAll={handleSelectAll}
            onSelectPayment={handleSelectPayment}
            onToggleExpand={handleToggleExpand}
            onDeleteSelected={handleDeleteSelected}
            onEdit={openEditWizard}
            onViewFiles={openFilesModal}
            onSchedulePayment={handleSchedulePayment}
            onRecordPayment={handleRecordPayablePayment}
            onEditDeductions={handleEditDeductions}
            canSelect={canEdit}
            canDelete={canEdit}
            canEdit={canEdit}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        )}


        <ResponseModal
          isOpen={responseModal.isOpen}
          message={responseModal.message}
          status={responseModal.status}
          title={responseModal.title}
          onClose={closeResponseModal}
        />

        {/* Payment Wizard Modal (for adding new payments) */}
        {wizardState.isOpen && (
          <PaymentWizard
            isOpen={wizardState.isOpen}
            onClose={closeWizard}
            paymentType={selectedTab}
            mode={wizardState.mode}
            paymentData={wizardState.paymentData}
            onSuccess={handleWizardSuccess}
          />
        )}

        {/* Payment Edit Modal (for editing existing payments) */}
        <PaymentEditModal
          isOpen={editModalState.isOpen}
          onClose={closeEditModal}
          paymentData={editModalState.paymentData}
          paymentType={selectedTab}
          onSuccess={handleEditModalSuccess}
        />

        <PaymentFilesModal
          isOpen={filesModalState.isOpen}
          onClose={closeFilesModal}
          payment={filesModalState.paymentData}
          paymentType={selectedTab}
        />
      </div>
    </Container>
    </>
  );
};

export default PaymentsPage;
